import { Component, DestroyRef, HostListener, OnInit, inject, input, model, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideX } from '@ng-icons/lucide';
import { Subject, debounceTime, of, switchMap, catchError } from 'rxjs';

import { ApiService } from '@mm-aggregator/drf-dynamics';

import { UI } from '../../components/ui';
import type { PaginatedResponse } from '../../services/provider.service';

/** Editable provider fields shared by the providers list modal and the provider hub General page. */
export interface ProviderFormValue {
  code: string;
  name: string;
  provider_type: string;
  adapter_class: string;
  financial_entity: string | null;
  country: string | null;
  currency: string | null;
  is_active: boolean;
}

export const PROVIDER_TYPES = [
  { value: 'FINANCIAL_ENTITY', label: 'Financial Entity' },
  { value: 'AGGREGATOR', label: 'Aggregator' },
  { value: 'GATEWAY', label: 'Gateway' },
  { value: 'PROXY', label: 'Proxy' },
] as const;

export function emptyProviderForm(): ProviderFormValue {
  return {
    code: '',
    name: '',
    provider_type: 'FINANCIAL_ENTITY',
    adapter_class: '',
    financial_entity: null,
    country: null,
    currency: null,
    is_active: true,
  };
}

interface LookupOption {
  id: string;
  code: string;
  name: string;
}

type LookupKey = 'entity' | 'country' | 'currency';

/**
 * Provider fields (code, name, type, adapter, entity, country, currency, active) with searchable lookups.
 * Two-way bound through `value`; the parent owns saving.
 */
@Component({
  selector: 'app-provider-form',
  standalone: true,
  imports: [FormsModule, NgIcon, ...UI],
  templateUrl: './provider-form.component.html',
  viewProviders: [provideIcons({ lucideX })],
})
export class ProviderFormComponent implements OnInit {
  private readonly api = inject(ApiService);

  readonly value = model.required<ProviderFormValue>();
  readonly disabled = input(false);
  readonly showAdapter = input(true);
  readonly showActive = input(true);
  readonly codeReadOnly = input(false);
  /** Texts shown in the lookups when editing an existing provider. */
  readonly entityLabel = input('');
  readonly countryLabel = input('');
  readonly currencyLabel = input('');
  /** Unique suffix for element ids when several forms exist on a page. */
  readonly idPrefix = input('provider-form');

  readonly providerTypes = PROVIDER_TYPES;

  private readonly destroyRef = inject(DestroyRef);
  private readonly entities = signal<LookupOption[]>([]);
  private readonly countries = signal<LookupOption[]>([]);
  private readonly currencies = signal<LookupOption[]>([]);
  /** Typed text per lookup; the API filters and pages (`?search=`), at most 20 rows come back. */
  private readonly lookup$: Record<LookupKey, Subject<string>> = {
    entity: new Subject<string>(),
    country: new Subject<string>(),
    currency: new Subject<string>(),
  };

  readonly search = signal<Record<LookupKey, string>>({ entity: '', country: '', currency: '' });
  readonly open = signal<LookupKey | null>(null);

  readonly filteredEntities = this.entities.asReadonly();
  readonly filteredCountries = this.countries.asReadonly();
  readonly filteredCurrencies = this.currencies.asReadonly();

  constructor() {
    this.bind('entity', 'finances/entities/', this.entities);
    this.bind('country', 'finances/countries/', this.countries);
    this.bind('currency', 'finances/currencies/', this.currencies);
  }

  ngOnInit(): void {
    this.search.set({
      entity: this.entityLabel(),
      country: this.countryLabel(),
      currency: this.currencyLabel(),
    });
    this.lookup$.entity.next('');
    this.lookup$.country.next('');
    this.lookup$.currency.next('');
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!(event.target as HTMLElement).closest('.autocomplete-wrapper')) {
      this.open.set(null);
    }
  }

  patch(changes: Partial<ProviderFormValue>): void {
    this.value.update((current) => ({ ...current, ...changes }));
  }

  setSearch(key: LookupKey, text: string): void {
    this.search.update((current) => ({ ...current, [key]: text }));
    this.open.set(key);
    this.lookup$[key].next(text.trim());
  }

  select(key: LookupKey, option: LookupOption): void {
    const label = `${option.code} — ${option.name}`;
    this.search.update((current) => ({ ...current, [key]: label }));
    this.patch(this.fieldFor(key, option.id));
    this.open.set(null);
  }

  clear(key: LookupKey): void {
    this.search.update((current) => ({ ...current, [key]: '' }));
    this.patch(this.fieldFor(key, null));
  }

  private fieldFor(key: LookupKey, id: string | null): Partial<ProviderFormValue> {
    switch (key) {
      case 'entity':
        return { financial_entity: id };
      case 'country':
        return { country: id };
      default:
        return { currency: id };
    }
  }

  private bind(key: LookupKey, path: string, target: { set(value: LookupOption[]): void }): void {
    this.lookup$[key]
      .pipe(
        debounceTime(250),
        switchMap((term) =>
          this.api
            .get<PaginatedResponse<LookupOption>>(path, { page: 1, page_size: 20, search: term })
            .pipe(catchError(() => of({ results: [] as LookupOption[] }))),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((res) => target.set(res.results));
  }
}
